import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Gauge, Lightbulb, Wrench, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const servicesData = {
  energioptimering: {
    icon: Gauge,
    title: 'Energioptimering',
    subtitle: 'LED og belysningsløsninger',
    image: '/images/film-grabs/still-4.jpg',
    description: 'Vi analyserer eksisterende belysningsinstallationer og implementerer energieffektive løsninger til erhvervsbygninger. Formålet er at reducere energiforbruget, forbedre lysforholdene og skabe en driftssikker belysningsløsning, der passer til bygningens anvendelse.',
    relatedLink: { href: '/ydelser/energitjek', label: 'Se vores Energitjek-tilbud' },
    benefits: [
      'Lavere energiforbrug i eksisterende belysningsinstallationer',
      'Moderne LED-løsninger tilpasset bygningens behov',
      'Forbedrede lysforhold i arbejdsområder',
      'Dokumentation af installation og løsning',
      'Implementering med minimal påvirkning af den daglige drift',
    ],
    process: [
      { step: '01', title: 'Analyse', desc: 'Vi gennemgår den eksisterende belysningsinstallation og vurderer energiforbrug, lysforhold og driftsbehov.' },
      { step: '02', title: 'Løsningsforslag', desc: 'På baggrund af analysen udarbejder vi et konkret forslag til en energieffektiv belysningsløsning.' },
      { step: '03', title: 'Implementering', desc: 'Vi installerer den nye løsning og sikrer, at arbejdet udføres effektivt og med minimal forstyrrelse af driften.' },
      { step: '04', title: 'Dokumentation', desc: 'Den færdige installation gennemgås og dokumenteres, så I har overblik over løsningen fremadrettet.' },
    ],
  },
  lysstyring: {
    icon: Lightbulb,
    title: 'Intelligent lysstyring',
    subtitle: 'Zoner, sensorer og automatisering',
    image: '/images/film-grabs/still-5.jpg',
    description: 'Intelligent lysstyring tilpasser belysningen efter aktivitet, dagslys og anvendelse af bygningen. Ved hjælp af sensorer, zonestyring og automatisering skabes en mere effektiv og fleksibel belysningsløsning til erhvervsbygninger.',
    benefits: [
      'Automatisk tilpasning af lys efter dagslys',
      'Bevægelsesstyret belysning i relevante områder',
      'Zonestyring tilpasset bygningens funktioner',
      'Mulighed for tidsplaner og scenarier',
      'Integration med eksisterende installationer',
    ],
    process: [
      { step: '01', title: 'Kortlægning', desc: 'Vi analyserer hvordan bygningen anvendes, og hvor intelligent styring kan skabe bedre udnyttelse af belysningen.' },
      { step: '02', title: 'Design', desc: 'Vi udarbejder en styringsstrategi med zoner, sensorer og styring tilpasset bygningens behov.' },
      { step: '03', title: 'Installation', desc: 'Sensorer, styringskomponenter og systemer installeres og integreres med den eksisterende belysning.' },
      { step: '04', title: 'Overdragelse', desc: 'Systemet programmeres og tilpasses, og brugerne introduceres til funktioner og styring.' },
    ],
  },
  service: {
    icon: Wrench,
    title: 'Service & vedligehold',
    subtitle: 'Driftssikre løsninger',
    image: '/images/film-grabs/still-6.jpg',
    description: 'Med en serviceaftale sikrer I stabil drift af jeres belysningsinstallationer. Vi planlægger vedligehold, håndterer akutte behov og følger løbende op på anlæggets tilstand.',
    benefits: [
      'Fast kontaktperson',
      'Planlagt forebyggende vedligehold',
      'Hurtig respons ved driftsproblemer',
      'Løbende gennemgang af installationer',
      'Dokumentation og overblik over anlæg',
    ],
    process: [
      { step: '01', title: 'Tilstandsvurdering', desc: 'Vi gennemgår jeres eksisterende installationer og vurderer behov for service og vedligehold.' },
      { step: '02', title: 'Serviceaftale', desc: 'Vi udarbejder en serviceaftale, der passer til jeres installationer og driftsbehov.' },
      { step: '03', title: 'Løbende service', desc: 'Vi udfører planlagt vedligehold og håndterer eventuelle driftsproblemer.' },
      { step: '04', title: 'Rapportering', desc: 'I får løbende overblik over installationernes tilstand og eventuelle anbefalinger.' },
    ],
  },
};

export const ServiceDetailPage = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];

  if (!service) {
    return (
      <main className="pt-32 pb-24">
        <div className="max-w-[1800px] mx-auto px-8 md:px-12">
          <p className="text-[#888]">Ydelse ikke fundet</p>
          <Link to="/" className="text-[#111] mt-4 inline-block">Tilbage til forsiden</Link>
        </div>
      </main>
    );
  }

  const Icon = service.icon;
  
  const seoTitles = {
    energioptimering: 'Energioptimering | LED-belysning til erhverv | Dilling El',
    lysstyring: 'Intelligent lysstyring | Sensorer og automatisering | Dilling El',
    service: 'Service & vedligehold | Driftssikre løsninger | Dilling El',
  };
  
  const seoDescriptions = {
    energioptimering: 'Vi analyserer eksisterende belysningsinstallationer og implementerer energieffektive LED-løsninger til erhvervsbygninger med dokumenteret effekt.',
    lysstyring: 'Intelligent lysstyring tilpasser belysningen efter aktivitet og dagslys. Sensorer, zonestyring og automatisering til erhvervsbygninger.',
    service: 'Med en serviceaftale sikrer I stabil drift af jeres belysningsinstallationer. Planlagt vedligehold og hurtig respons ved driftsproblemer.',
  };

  return (
    <main data-testid="service-detail-page" className="pt-24">
      <Helmet>
        <title>{seoTitles[serviceId] || `${service.title} | Dilling El`}</title>
        <meta name="description" content={seoDescriptions[serviceId] || service.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://dillingel.dk/ydelser/${serviceId}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://dillingel.dk/ydelser/${serviceId}`} />
        <meta property="og:title" content={seoTitles[serviceId] || `${service.title} | Dilling El`} />
        <meta property="og:description" content={seoDescriptions[serviceId] || service.description} />
        <meta property="og:image" content="https://dillingel.dk/images/og-image.jpg" />
        <meta property="og:locale" content="da_DK" />
        <meta property="og:site_name" content="Dilling El ApS" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`https://dillingel.dk/ydelser/${serviceId}`} />
        <meta property="twitter:title" content={seoTitles[serviceId] || `${service.title} | Dilling El`} />
        <meta property="twitter:description" content={seoDescriptions[serviceId] || service.description} />
        <meta property="twitter:image" content="https://dillingel.dk/images/og-image.jpg" />
      </Helmet>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="w-16 h-16 flex items-center justify-center mb-8">
                <Icon size={48} strokeWidth={1} className="text-[#0E3D24]" />
              </div>
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                {service.subtitle}
              </span>
              <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#0E3D24] mt-4 leading-[1.1]">
                {service.title}
              </h1>
              <p className="text-[#1E2A24]/70 text-lg mt-6 leading-relaxed max-w-xl">
                {service.description}
              </p>
              {service.relatedLink && (
                <Link 
                  to={service.relatedLink.href}
                  className="inline-flex items-center gap-3 mt-6 text-[#1F5A3A] hover:text-[#0E3D24] text-sm tracking-[0.05em] group transition-colors"
                >
                  <span className="link-underline">{service.relatedLink.label}</span>
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              )}
              <Link 
                to="/kontakt"
                className="inline-flex items-center gap-3 mt-8 text-[#1F5A3A] hover:text-[#0E3D24] text-sm tracking-[0.1em] uppercase group transition-colors"
              >
                <span className="link-underline">Få et tilbud</span>
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img loading="lazy" 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits - Hvad I får */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
                Fordele
              </span>
              <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
                Hvad I får
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="space-y-4"
            >
              {service.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <CheckCircle2 size={20} className="text-[#0E3D24] mt-0.5 flex-shrink-0" />
                  <span className="text-[#1E2A24]/70">{benefit}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process - Sådan arbejder vi */}
      <section className="py-24 md:py-32 bg-[#F4F2EE]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#A6B8A2]">
              Proces
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-medium text-[#0E3D24] mt-6">
              Sådan arbejder vi
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <span className="text-[#A6B8A2] text-sm">{item.step}</span>
                <h3 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1E2A24] mt-2 mb-3">
                  {item.title}
                </h3>
                <p className="text-[#1E2A24]/70 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0E3D24]">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-medium text-white mb-8">
              Klar til at komme i gang?
            </h2>
            <Link 
              to="/kontakt"
              className="inline-flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase bg-white text-[#0E3D24] px-8 py-4 hover:bg-[#F4F2EE] transition-colors group"
            >
              <span>Kontakt os</span>
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ServiceDetailPage;
